import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MapPin, User, Calendar as CalendarIcon, GripVertical } from "lucide-react";

const COLUMNS = [
  { key: "pending", label: "Pending", color: "border-t-yellow-400" },
  { key: "confirmed", label: "Confirmed", color: "border-t-blue-400" },
  { key: "in_progress", label: "In Progress", color: "border-t-purple-400" },
  { key: "completed", label: "Completed", color: "border-t-green-500" },
  { key: "cancelled", label: "Cancelled", color: "border-t-slate-400" },
];

export default function BookingsKanban({ bookings, onMove, crew = [], onAssignCrew }) {
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const bookingsByStatus = COLUMNS.reduce((acc, col) => {
    acc[col.key] = bookings.filter(
      (b) => (b.status || "pending") === col.key
    );
    return acc;
  }, {});

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };

  const onDragEnd = () => {
    setDragId(null);
    setDragOverCol(null);
  };

  const onDragOver = (e, colKey) => {
    e.preventDefault();
    setDragOverCol(colKey);
  };

  const onDrop = (e, colKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    if (id) {
      const booking = bookings.find((b) => b.id === id);
      if (booking && booking.status !== colKey) {
        onMove(id, colKey);
      }
    }
    setDragId(null);
    setDragOverCol(null);
  };

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3"
      data-testid="bookings-kanban-board"
    >
      {COLUMNS.map((col) => {
        const items = bookingsByStatus[col.key];
        return (
          <div
            key={col.key}
            onDragOver={(e) => onDragOver(e, col.key)}
            onDrop={(e) => onDrop(e, col.key)}
            className={`flex flex-col rounded-lg border bg-slate-50 border-t-4 ${col.color} ${
              dragOverCol === col.key
                ? "border-orange-400 ring-2 ring-orange-200"
                : "border-slate-200"
            }`}
            data-testid={`kanban-column-${col.key}`}
          >
            <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-white rounded-t-lg">
              <p className="text-sm font-semibold text-slate-700">{col.label}</p>
              <Badge className="bg-slate-200 text-slate-700">{items.length}</Badge>
            </div>
            <div className="flex-1 p-2 space-y-2 min-h-[200px] max-h-[600px] overflow-y-auto">
              {items.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-6">No bookings</p>
              )}
              {items.map((b) => (
                <div
                  key={b.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, b.id)}
                  onDragEnd={onDragEnd}
                  className={`bg-white rounded-md border border-slate-200 p-3 shadow-sm cursor-move hover:shadow-md transition-shadow ${
                    dragId === b.id ? "opacity-50" : ""
                  }`}
                  data-testid={`kanban-card-${b.id}`}
                >
                  <div className="flex items-start gap-1 mb-1">
                    <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {b.customer_name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {b.service_name}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 space-y-1 mt-2">
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" />
                      <span>{b.preferred_date}</span>
                      <span className="text-slate-400">·</span>
                      <span>{b.preferred_time}</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-500">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{b.community || b.island}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-1">
                    <User className="w-3 h-3 text-slate-400" />
                    <Select
                      value={b.crew_id || "_unassigned"}
                      onValueChange={(v) =>
                        onAssignCrew(b.id, v === "_unassigned" ? null : v)
                      }
                    >
                      <SelectTrigger
                        className="h-7 text-xs px-2 border-slate-200 flex-1"
                        data-testid={`kanban-crew-select-${b.id}`}
                      >
                        <SelectValue placeholder="Assign crew" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_unassigned">Unassigned</SelectItem>
                        {crew
                          .filter((c) => c.is_active !== false)
                          .map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
